import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { setAuthResponse, logout } from '@/entities/auth/model/slice';
import type { AuthResponse } from '@/entities/auth/model/types';

import type { RootState } from '@/app/types';
import { API_BASE_URL } from '@/shared/config';

/**
 * Singleton pattern for refresh management
 */
class RefreshManager {
  private static instance: RefreshManager;
  private isRefreshing = false;
  private refreshPromise: Promise<AuthResponse | null> | null = null;

  /**
   * Get the singleton instance of the RefreshManager
   */
  static getInstance(): RefreshManager {
    if (!RefreshManager.instance) {
      RefreshManager.instance = new RefreshManager();
    }
    return RefreshManager.instance;
  }

  /**
   * Execute the token refresh process
   * @param refreshFn Function that performs the token refresh and returns a promise
   * @returns The new access token or null if refresh failed
   */
  async executeRefresh(refreshFn: () => Promise<AuthResponse | null>): Promise<AuthResponse | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = refreshFn().finally(() => {
      this.isRefreshing = false;
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  get refreshing(): boolean {
    return this.isRefreshing;
  }
}

const refreshManager = RefreshManager.getInstance();

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

/**
 * Base query with re-authentication
 * @param args - The query arguments
 * @param api - The API instance
 * @param extraOptions - Additional options for the query
 * @returns The result of the query
 */
export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const endpoint = typeof args === 'string' ? args : args.url;
  if (endpoint?.includes('/auth/')) {
    return baseQuery(args, api, extraOptions);
  }

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (refreshManager.refreshing) {
      return result;
    }

    const refreshResult = await refreshManager.executeRefresh(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (response.ok) {
          const data: AuthResponse = await response.json();
          api.dispatch(setAuthResponse(data));
          return data;
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }

      api.dispatch(logout());
      return null;
    });

    if (refreshResult) {
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
